/**
 * FIKO MATERIAL POOL
 * ==================
 * Material pooling/caching system to prevent duplicate material creation.
 *
 * Problem: The 3D engine creates many duplicate materials (same color, same
 * properties). Each material is a GPU resource. Shared materials reduce GPU
 * memory and draw calls.
 *
 * Features:
 * - Property-hash based deduplication (identical props = same material)
 * - Reference counting per material (safe multi-consumer sharing)
 * - LRU eviction for materials unused > 60 seconds
 * - Singleton pattern (engine-wide single pool)
 * - Supports MeshStandardMaterial, LineBasicMaterial, LineMaterial
 * - Zero race conditions (synchronous API, single-threaded JS)
 *
 * Usage:
 *   const pool = FikoMaterialPool.getInstance();
 *   const mat = pool.getStandard({ color: 0xff0000, roughness: 0.5 });
 *   // ... use mat ...
 *   pool.release(mat); // decrements ref count
 */

import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// ============================================================================
// INTERFACES
// ============================================================================

export interface StandardMaterialProps {
    color: number;
    roughness?: number;
    metalness?: number;
    transparent?: boolean;
    opacity?: number;
    side?: THREE.Side;
    flatShading?: boolean;
    depthTest?: boolean;
    depthWrite?: boolean;
}

export interface LineBasicMaterialProps {
    color: number;
    linewidth?: number;
    transparent?: boolean;
    opacity?: number;
    depthTest?: boolean;
    depthWrite?: boolean;
}

export interface LineMaterialProps {
    color: number;
    linewidth?: number;
    transparent?: boolean;
    opacity?: number;
    depthTest?: boolean;
    depthWrite?: boolean;
    worldUnits?: boolean;
    dashed?: boolean;
    dashScale?: number;
    dashSize?: number;
    gapSize?: number;
    resolution?: THREE.Vector2;
}

export interface MaterialPoolStats {
    totalMaterials: number;
    uniqueMaterials: number;
    savedAllocations: number;
    totalRefCount: number;
    evictedCount: number;
    memoryEstimateBytes: number;
}

// ============================================================================
// INTERNAL ENTRY
// ============================================================================

interface PoolEntry {
    material: THREE.Material;
    refCount: number;
    lastAccessTime: number;
    key: string;
    type: 'standard' | 'linebasic' | 'linematerial';
}

// ============================================================================
// FIKO MATERIAL POOL
// ============================================================================

export class FikoMaterialPool {
    private static _instance: FikoMaterialPool | null = null;

    // Cache: hash key -> pool entry
    private readonly _cache = new Map<string, PoolEntry>();

    // Reverse lookup: material -> key (for release by material reference)
    private readonly _materialToKey = new Map<THREE.Material, string>();

    // Stats
    private _savedAllocations = 0;
    private _evictedCount = 0;

    // Cleanup interval
    private _cleanupTimer: ReturnType<typeof setInterval> | null = null;
    private static readonly DEFAULT_MAX_AGE_MS = 60_000; // 60 seconds LRU
    private static readonly CLEANUP_INTERVAL_MS = 30_000; // check every 30s

    // ========================================================================
    // SINGLETON
    // ========================================================================

    private constructor() {
        // Auto-cleanup interval
        this._cleanupTimer = setInterval(() => {
            this.cleanup();
        }, FikoMaterialPool.CLEANUP_INTERVAL_MS);
    }

    static getInstance(): FikoMaterialPool {
        if (!FikoMaterialPool._instance) {
            FikoMaterialPool._instance = new FikoMaterialPool();
        }
        return FikoMaterialPool._instance;
    }

    // ========================================================================
    // PUBLIC API: GET MATERIALS
    // ========================================================================

    /**
     * Get or create a MeshStandardMaterial with the given properties.
     * If a material with identical properties already exists, returns the
     * cached instance (incrementing its ref count).
     */
    getStandard(props: StandardMaterialProps): THREE.MeshStandardMaterial {
        const key = this._hashStandard(props);
        const existing = this._cache.get(key);
        if (existing) {
            existing.refCount++;
            existing.lastAccessTime = performance.now();
            this._savedAllocations++;
            // Clone on acquire to prevent aliasing: callers can safely mutate
            // their copy without affecting other consumers of the same template.
            const clone = (existing.material as THREE.MeshStandardMaterial).clone();
            this._materialToKey.set(clone, key);
            return clone;
        }

        const mat = new THREE.MeshStandardMaterial({
            color: props.color,
            roughness: props.roughness ?? 0.5,
            metalness: props.metalness ?? 0.5,
            transparent: props.transparent ?? false,
            opacity: props.opacity ?? 1.0,
            side: props.side ?? THREE.FrontSide,
            flatShading: props.flatShading ?? false,
            depthTest: props.depthTest ?? true,
            depthWrite: props.depthWrite ?? true,
        });

        const entry: PoolEntry = {
            material: mat,
            refCount: 1,
            lastAccessTime: performance.now(),
            key,
            type: 'standard',
        };
        this._cache.set(key, entry);
        this._materialToKey.set(mat, key);
        return mat;
    }

    /**
     * Get or create a LineBasicMaterial with the given properties.
     */
    getLineBasic(props: LineBasicMaterialProps): THREE.LineBasicMaterial {
        const key = this._hashLineBasic(props);
        const existing = this._cache.get(key);
        if (existing) {
            existing.refCount++;
            existing.lastAccessTime = performance.now();
            this._savedAllocations++;
            // Clone on acquire to prevent aliasing
            const clone = (existing.material as THREE.LineBasicMaterial).clone();
            this._materialToKey.set(clone, key);
            return clone;
        }

        const mat = new THREE.LineBasicMaterial({
            color: props.color,
            linewidth: props.linewidth ?? 1,
            transparent: props.transparent ?? false,
            opacity: props.opacity ?? 1.0,
            depthTest: props.depthTest ?? true,
            depthWrite: props.depthWrite ?? true,
        });

        const entry: PoolEntry = {
            material: mat,
            refCount: 1,
            lastAccessTime: performance.now(),
            key,
            type: 'linebasic',
        };
        this._cache.set(key, entry);
        this._materialToKey.set(mat, key);
        return mat;
    }

    /**
     * Get or create a LineMaterial (fat lines for Line2).
     */
    getLineMaterial(props: LineMaterialProps): LineMaterial {
        const key = this._hashLineMaterial(props);
        const existing = this._cache.get(key);
        if (existing) {
            existing.refCount++;
            existing.lastAccessTime = performance.now();
            this._savedAllocations++;
            // Clone on acquire to prevent aliasing
            const clone = (existing.material as LineMaterial).clone() as LineMaterial;
            this._materialToKey.set(clone, key);
            return clone;
        }

        const mat = new LineMaterial({
            color: props.color,
            linewidth: props.linewidth ?? 2,
            transparent: props.transparent ?? false,
            opacity: props.opacity ?? 1.0,
            depthTest: props.depthTest ?? true,
            depthWrite: props.depthWrite ?? true,
            worldUnits: props.worldUnits ?? false,
            dashed: props.dashed ?? false,
            dashScale: props.dashScale ?? 1,
            dashSize: props.dashSize ?? 1,
            gapSize: props.gapSize ?? 1,
        });

        if (props.resolution) {
            mat.resolution.copy(props.resolution);
        }

        const entry: PoolEntry = {
            material: mat,
            refCount: 1,
            lastAccessTime: performance.now(),
            key,
            type: 'linematerial',
        };
        this._cache.set(key, entry);
        this._materialToKey.set(mat, key);
        return mat;
    }

    // ========================================================================
    // PUBLIC API: RELEASE & LIFECYCLE
    // ========================================================================

    /**
     * Release a material reference. Decrements ref count on the template.
     * If the material is a clone (not the template itself), it is disposed.
     * When ref count reaches 0, the template becomes eligible for LRU eviction.
     */
    release(material: THREE.Material): void {
        const key = this._materialToKey.get(material);
        if (!key) return; // not managed by this pool

        const entry = this._cache.get(key);
        if (!entry) return;

        entry.refCount = Math.max(0, entry.refCount - 1);
        entry.lastAccessTime = performance.now();

        // If this is a clone (not the template), dispose it and remove from reverse map
        if (material !== entry.material) {
            this._materialToKey.delete(material);
            try { material.dispose(); } catch (_) { /* already disposed */ }
        }
    }

    /**
     * Force-release a material, disposing it immediately regardless of ref count.
     * If the material is a clone, only the clone is disposed (template stays).
     * If the material is the template itself, the entire cache entry is removed.
     */
    forceRelease(material: THREE.Material): void {
        const key = this._materialToKey.get(material);
        if (!key) return;

        const entry = this._cache.get(key);
        if (!entry) return;

        if (material !== entry.material) {
            // This is a clone - dispose clone only, decrement ref count
            this._materialToKey.delete(material);
            try { material.dispose(); } catch (_) { /* already disposed */ }
            entry.refCount = Math.max(0, entry.refCount - 1);
        } else {
            // This is the template - remove entire cache entry
            this._disposeEntry(entry);
            this._cache.delete(key);
            this._materialToKey.delete(material);
        }
    }

    /**
     * Cleanup unused materials (ref count = 0) older than maxAge.
     * Called automatically every 30 seconds. Can also be called manually.
     */
    cleanup(maxAge: number = FikoMaterialPool.DEFAULT_MAX_AGE_MS): void {
        const now = performance.now();
        const keysToRemove: string[] = [];

        for (const [key, entry] of this._cache) {
            if (entry.refCount === 0 && (now - entry.lastAccessTime) > maxAge) {
                keysToRemove.push(key);
            }
        }

        for (const key of keysToRemove) {
            const entry = this._cache.get(key);
            if (entry) {
                this._materialToKey.delete(entry.material);
                this._disposeEntry(entry);
                this._cache.delete(key);
                this._evictedCount++;
            }
        }
    }

    /**
     * Get pool statistics.
     */
    getStats(): MaterialPoolStats {
        let totalRefCount = 0;
        for (const entry of this._cache.values()) {
            totalRefCount += entry.refCount;
        }

        const uniqueMaterials = this._cache.size;
        // Rough estimate: ~1KB per material (textures not counted)
        const memoryEstimateBytes = uniqueMaterials * 1024;

        return {
            totalMaterials: totalRefCount,
            uniqueMaterials,
            savedAllocations: this._savedAllocations,
            totalRefCount,
            evictedCount: this._evictedCount,
            memoryEstimateBytes,
        };
    }

    /**
     * Check if a material is managed by this pool.
     */
    isManaged(material: THREE.Material): boolean {
        return this._materialToKey.has(material);
    }

    /**
     * Get the current ref count for a material.
     */
    getRefCount(material: THREE.Material): number {
        const key = this._materialToKey.get(material);
        if (!key) return 0;
        const entry = this._cache.get(key);
        return entry ? entry.refCount : 0;
    }

    /**
     * Dispose all cached materials and reset the pool.
     * After calling dispose(), the singleton can still be used -
     * it will simply start with an empty cache.
     */
    dispose(): void {
        for (const entry of this._cache.values()) {
            this._disposeEntry(entry);
        }
        this._cache.clear();
        this._materialToKey.clear();
        this._savedAllocations = 0;
        this._evictedCount = 0;

        if (this._cleanupTimer !== null) {
            clearInterval(this._cleanupTimer);
            this._cleanupTimer = null;
        }

        FikoMaterialPool._instance = null;
    }

    // ========================================================================
    // PRIVATE: HASHING
    // ========================================================================

    private _hashStandard(p: StandardMaterialProps): string {
        // Format: std_COLOR_ROUGH_METAL_TRANSPARENT_OPACITY_SIDE_FLAT_DTEST_DWRITE
        return `std_${(p.color >>> 0).toString(16)}_${(p.roughness ?? 0.5).toFixed(2)}_${(p.metalness ?? 0.5).toFixed(2)}_${p.transparent ? 1 : 0}_${(p.opacity ?? 1).toFixed(2)}_${p.side ?? 0}_${p.flatShading ? 1 : 0}_${(p.depthTest ?? true) ? 1 : 0}_${(p.depthWrite ?? true) ? 1 : 0}`;
    }

    private _hashLineBasic(p: LineBasicMaterialProps): string {
        return `lb_${(p.color >>> 0).toString(16)}_${p.linewidth ?? 1}_${p.transparent ? 1 : 0}_${(p.opacity ?? 1).toFixed(2)}_${(p.depthTest ?? true) ? 1 : 0}_${(p.depthWrite ?? true) ? 1 : 0}`;
    }

    private _hashLineMaterial(p: LineMaterialProps): string {
        return `lm_${(p.color >>> 0).toString(16)}_${p.linewidth ?? 2}_${p.transparent ? 1 : 0}_${(p.opacity ?? 1).toFixed(2)}_${(p.depthTest ?? true) ? 1 : 0}_${(p.depthWrite ?? true) ? 1 : 0}_${p.worldUnits ? 1 : 0}_${p.dashed ? 1 : 0}_${p.dashScale ?? 1}_${p.dashSize ?? 1}_${p.gapSize ?? 1}`;
    }

    // ========================================================================
    // PRIVATE: DISPOSAL
    // ========================================================================

    private _disposeEntry(entry: PoolEntry): void {
        try {
            entry.material.dispose();
        } catch (_) {
            // Material may already be disposed - ignore
        }
    }
}

// ============================================================================
// CONVENIENCE SINGLETON ACCESSOR
// ============================================================================

/** Shorthand: `materialPool.getStandard(...)` */
export const materialPool = FikoMaterialPool.getInstance();

/** Get the singleton instance */
export function getMaterialPool(): FikoMaterialPool {
    return FikoMaterialPool.getInstance();
}

/** Dispose the global material pool */
export function disposeMaterialPool(): void {
    FikoMaterialPool.getInstance().dispose();
}
